/* ============================================================
   NovaBreak Card Show — global.js
   Helpers + card-in-hand 3D + Ajax cart + cart drawer
   ============================================================ */
window.NB = (function () {
  const cfg = window.NBConfig || {};

  /* ---------- helpers ---------- */
  const MONEY = v => {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency.active) || 'USD' })
        .format(v);
    } catch (e) { return '$' + Number(v).toFixed(2); }
  };
  const MONEY_CENTS = c => MONEY(c / 100);
  const esc = s => String(s).replace(/[&<>"']/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));
  const sized = (url, w) => url ? url + (url.includes('?') ? '&' : '?') + 'width=' + w : '';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Condition normalization */
  const COND_ORDER = ['NM', 'LP', 'MP', 'HP', 'DMG'];
  function condCode(raw) {
    const r = String(raw || '').trim().toLowerCase();
    if (!r) return '';
    if (r === 'nm' || r.startsWith('near mint') || r.startsWith('mint')) return 'NM';
    if (r === 'lp' || r.startsWith('light')) return 'LP';
    if (r === 'mp' || r.startsWith('moderate') || r.startsWith('played')) return 'MP';
    if (r === 'hp' || r.startsWith('heav')) return 'HP';
    if (r === 'dmg' || r.startsWith('damag')) return 'DMG';
    return String(raw).toUpperCase().slice(0, 6);
  }
  const condClass = code => COND_ORDER.includes(code) ? code : 'OTHER';

  /* Parse a raw products.json product into a card model */
  function toCard(p, i) {
    const tags = p.tags || [];
    const lower = t => t.toLowerCase();
    const setPre = (cfg.setPrefix || 'set:').toLowerCase();
    const condPre = (cfg.condPrefix || 'condition:').toLowerCase();
    const gradePre = (cfg.gradePrefix || 'grade:').toLowerCase();
    const slabTag = (cfg.slabTag || 'slab').toLowerCase();

    const isSlab = tags.some(t => lower(t) === slabTag) ||
      tags.some(t => lower(t).startsWith(gradePre));
    const gradeTag = tags.find(t => lower(t).startsWith(gradePre));

    const optNames = (p.options || []).map(o => String(o.name || o).toLowerCase());
    const condIdx = optNames.indexOf('condition');
    const gradeIdx = optNames.indexOf('grade');
    const condTag = tags.find(t => lower(t).startsWith(condPre));
    const tagCond = condTag ? condCode(condTag.slice(condPre.length)) : '';

    const variants = (p.variants || []).map(v => {
      const opts = [v.option1, v.option2, v.option3];
      /* Shopify products.json exposes inventory_quantity and inventory_policy
         ('deny' = can't oversell, 'continue' = allow). Capture both so the
         quantity stepper can cap at what's actually in stock. */
      const policy = (v.inventory_policy || 'deny').toLowerCase();
      const mgmt = v.inventory_management; // null = not tracked → unlimited; undefined = unknown until /products/HANDLE.js
      let stock = (typeof v.inventory_quantity === 'number') ? v.inventory_quantity : null;
      const oversell = policy === 'continue' || mgmt === null;
      return {
        id: v.id,
        price: parseFloat(v.price || 0),
        available: !!v.available,
        stock: stock,            // units in stock (may be null if untracked)
        oversell: oversell,      // true = no hard cap
        cond: condIdx >= 0 ? condCode(opts[condIdx]) : tagCond,
        grade: gradeIdx >= 0 ? String(opts[gradeIdx] || '') :
          (gradeTag ? gradeTag.slice(gradePre.length).trim() : '')
      };
    });
    const setTag = tags.find(t => lower(t).startsWith(setPre));
    const eraPre = (cfg.eraPrefix || 'era:').toLowerCase();
    const eraTag = tags.find(t => lower(t).startsWith(eraPre));
    const numStr = (p.title || '').match(/(\d{1,4}[A-Za-z]*\s*\/\s*[A-Za-z]*\d{1,4}|[A-Z]{2,}\d{1,4})/);
    const finishTag = tags.find(t => lower(t).startsWith('finish:'));
    const numMatch = (p.title || '').match(/(\d{1,4})\s*\/\s*\d{1,4}/) ||
      ((p.variants && p.variants[0] && p.variants[0].sku) || '').match(/\d{1,4}/);
    return {
      id: p.id, idx: i,
      name: p.title, handle: p.handle, url: '/products/' + p.handle,
      variants: variants, isSlab: isSlab,
      img: p.images && p.images[0] ? p.images[0].src : null,
      back: p.images && p.images[1] ? p.images[1].src : null,
      tcg: p.vendor || '',
      set: setTag ? setTag.slice(setPre.length).trim() : '',
      era: eraTag ? eraTag.slice(eraPre.length).trim() : '',
      finish: finishTag ? finishTag.slice(7).trim() : '',
      numStr: numStr ? numStr[0].replace(/\s/g, '') : '',
      num: numMatch ? parseInt(numMatch[1], 10) : 9999,
      sold: !variants.some(v => v.available)
    };
  }
  const bestVar = c => c.variants.find(v => v.available) || c.variants[0] || { price: 0, cond: '', grade: '' };

  /* Badge for a card: grade beats condition */
  function badgeHTML(c) {
    const bv = bestVar(c);
    /* Slabs keep the prominent grade badge on the image (not redundant — slabs
       read differently). Raw cards show condition only as a caption chip below,
       so no on-image condition badge (it duplicated the chip). */
    if (c.isSlab && bv.grade) return '<span class="nb-grade">' + esc(bv.grade) + '</span>';
    return '';
  }

  /* Chips under the card name: the structured, filterable attributes.
     The collector number stays in the title, so it's NOT chipped.
     Order: set, finish, then condition/grade (grade for slabs). */
  function chipsHTML(c) {
    const bv = bestVar(c);
    let h = '';
    if (c.set)    h += '<span class="nb-chip nb-chip--set">' + esc(c.set) + '</span>';
    if (c.finish) h += '<span class="nb-chip nb-chip--finish">' + esc(c.finish) + '</span>';
    if (c.isSlab && bv.grade) {
      h += '<span class="nb-chip nb-chip--grade">' + esc(bv.grade) + '</span>';
    } else if (bv.cond) {
      h += '<span class="nb-chip nb-chip--cond ' + condClass(bv.cond) + '">' + esc(bv.cond) + '</span>';
    }
    return h;
  }

  /* ---------- Card-in-hand 3D (tilt + flip) ---------- */
  /* root: .nb-card3d containing .nb-card > .nb-cardface.front/.back imgs */
  function Card3D(root, opts) {
    opts = opts || {};
    const card = root.querySelector('.nb-card');
    let flipped = false, tx = 0, ty = 0;
    let zoom = 1, panX = 0, panY = 0;
    const hasBack = () => !!(opts.hasBack ? opts.hasBack() : root.dataset.hasBack === 'true');
    const apply = () => {
      card.style.transform = 'rotateY(' + ((flipped ? 180 : 0) + ty) + 'deg) rotateX(' + tx + 'deg)';
    };
    const applyZoom = () => {
      card.style.setProperty('--zoom', zoom);
      card.style.setProperty('--panX', panX + 'px');
      card.style.setProperty('--panY', panY + 'px');
      root.classList.toggle('is-zoomed', zoom > 1.02);
      setFace();
    };
    const setFace = () => {
      /* Only cull the opposite face while zoomed — during a normal (unzoomed)
         flip we must NOT hide a face or the card vanishes mid-rotation. */
      if (zoom > 1.02) {
        root.dataset.face = flipped ? 'back' : 'front';
      } else {
        root.removeAttribute('data-face');
      }
    };
    const flip = () => {
      if (!hasBack()) return;
      flipped = !flipped; apply();
      setFace();
      if (opts.onFlip) opts.onFlip(flipped);
    };
    /* Desktop tilt */
    root.addEventListener('mousemove', e => {
      if (reduced || zoom > 1.02) return;
      const r = root.getBoundingClientRect();
      ty = ((e.clientX - r.left) / r.width - .5) * 16;
      tx = -((e.clientY - r.top) / r.height - .5) * 14;
      card.style.transition = 'transform .08s linear';
      apply();
    });
    root.addEventListener('mouseleave', () => {
      tx = 0; ty = 0;
      card.style.transition = 'transform .6s cubic-bezier(.2,.7,.25,1)';
      apply();
    });

    card.addEventListener('click', () => { if (zoom <= 1.02) flip(); });
    card.addEventListener('keydown', e => { if (e.key === 'Enter') flip(); });
    return {
      flip: flip,
      reset: () => { flipped = false; tx = 0; ty = 0; zoom = 1; panX = 0; panY = 0; apply(); applyZoom(); },
      isFlipped: () => flipped,
      resetZoom: () => { zoom = 1; panX = 0; panY = 0; applyZoom(); }
    };
  }

  /* ---------- Ajax cart ---------- */
  function cartErrorMessage(payload, fallback) {
    if (!payload) return fallback || 'Quantity unavailable.';
    return payload.description || payload.message || payload.status_message || fallback || 'Quantity unavailable.';
  }
  async function cartAdd(variantId, qty) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) throw new Error(cartErrorMessage(payload, 'We couldn’t add that quantity. There may not be enough stock available.'));
    await refreshCart();
    if (cfg.cartDrawer !== false) openDrawer();
    return payload;
  }
  async function cartChange(lineKey, qty) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: qty })
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) throw new Error(cartErrorMessage(payload, 'We couldn’t update that quantity. There may not be enough stock available.'));
    await refreshCart();
    return payload;
  }
  async function getCart() {
    return (await fetch('/cart.js')).json();
  }

  /* ---------- Cart drawer ---------- */
  const drawer = () => document.getElementById('nb-drawer');
  const drawerBack = () => document.getElementById('nb-drawer-back');

  /* Reliable per-variant stock comes from Liquid (server-side), NOT the public
     /products.js endpoint — Shopify withholds inventory_quantity there to stop
     stock scraping, so it reads null/0 and must never be trusted for a cap.
     window.NB_STOCK (cart items) and the binder inventory manifest feed this. */
  const STOCK = {};
  function registerStock(map) {
    if (!map) return;
    for (const id in map) {
      if (Object.prototype.hasOwnProperty.call(map, id)) STOCK[String(id)] = map[id];
    }
  }
  if (window.NB_STOCK) registerStock(window.NB_STOCK);
  function knownMax(variantId) {
    const s = STOCK[String(variantId)];
    if (!s) return null; /* unknown → keep whatever cap is already on the line */
    if (!s.tracked || String(s.policy).toLowerCase() === 'continue') return Infinity;
    return Math.max(0, Number(s.stock) || 0);
  }
  function capMessage(max) {
    if (max <= 0) return 'This card is currently out of stock.';
    return 'Only ' + max + ' available. Quantity has been adjusted.';
  }
  function showLineError(line, msg) {
    if (!line) return;
    let el = line.querySelector('[data-cart-error]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'nb-cart-error';
      el.setAttribute('data-cart-error','');
      const qty = line.querySelector('.nb-qty');
      if (qty) qty.insertAdjacentElement('afterend', el);
    }
    el.textContent = msg || '';
    el.hidden = !msg;
  }
  function lineMax(line) {
    if (!line) return Infinity;
    const raw = line.dataset.max;
    if (raw === '' || raw == null || raw === 'null' || raw === 'unlimited') return Infinity;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? Math.max(0, n) : Infinity;
  }
  function syncLineQty(line) {
    if (!line) return;
    const input = line.querySelector('[data-act="qty"]');
    const inc = line.querySelector('[data-act="inc"]');
    const dec = line.querySelector('[data-act="dec"]');
    if (!input) return;
    const max = lineMax(line);
    let val = parseInt(input.value, 10);
    if (!Number.isFinite(val) || val < 0) val = 0;
    if (max !== Infinity && val > max) { val = max; input.value = val; showLineError(line, capMessage(max)); }
    if (max !== Infinity) input.max = String(max); else input.removeAttribute('max');
    if (inc) inc.disabled = max !== Infinity && val >= max;
    if (dec) dec.disabled = val <= 0;
  }
  function hydrateLineStock(line) {
    if (!line || !line.dataset.variantId) { syncLineQty(line); return; }
    const m = knownMax(line.dataset.variantId);
    if (m !== null) {
      /* Reliable Liquid-sourced cap: real number, or unlimited when untracked/continue. */
      line.dataset.max = (m === Infinity) ? 'unlimited' : String(m);
    }
    /* If unknown, keep whatever is already on the line: the accurate Liquid
       data-max on the cart page, or 'unlimited' in the drawer. Never force 1. */
    syncLineQty(line);
  }
  function hydrateCartStock(scope) {
    (scope || document).querySelectorAll('.nb-cart-line').forEach(hydrateLineStock);
  }

  function lineHTML(item) {
    const v = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    /* render the condition/grade as a chip for scannability */
    const chip = v ? '<span class="nb-chip nb-chip--cond ' + condClass(condCode(v)) + '">' + esc(v) + '</span>' : '';
    return '<div class="nb-cart-line" data-key="' + item.key + '" data-handle="' + esc(item.handle || '') + '" data-variant-id="' + item.variant_id + '" data-max="unlimited">' +
      '<img src="' + sized(item.image, 200) + '" alt="">' +
      '<div>' +
        '<a class="ttl" href="' + item.url + '">' + esc(item.product_title) + '</a>' +
        (chip ? '<div class="nb-cart-line__chips">' + chip + '</div>' : '') +
        '<div class="nb-qty">' +
          '<button type="button" data-act="dec" aria-label="Decrease">−</button>' +
          '<input value="' + item.quantity + '" inputmode="numeric" aria-label="Quantity" data-act="qty">' +
          '<button type="button" data-act="inc" aria-label="Increase">+</button>' +
        '</div>' +
        '<button type="button" class="rm" data-act="rm">Remove</button>' +
      '</div>' +
      '<div class="pr">' + MONEY_CENTS(item.final_line_price) + '</div>' +
    '</div>';
  }

  /* Free-shipping progress bar for the drawer. Threshold read from a global
     set on window by the theme (settings), default $75. */
  function shipBarHTML(cart) {
    const thr = (window.NB_FREE_SHIP || 75) * 100;
    if (thr <= 0) return '';
    const rem = thr - cart.total_price;
    let pct = Math.min(100, Math.round(cart.total_price * 100 / thr));
    if (rem <= 0) {
      return '<div class="nb-shipbar is-complete"><p class="nb-shipbar__msg">🎉 Free shipping unlocked!</p>' +
        '<div class="nb-shipbar__track"><div class="nb-shipbar__fill" style="width:100%"></div></div></div>';
    }
    return '<div class="nb-shipbar"><p class="nb-shipbar__msg">You\u2019re <strong>' + MONEY_CENTS(rem) +
      '</strong> from free shipping</p><div class="nb-shipbar__track"><div class="nb-shipbar__fill" style="width:' +
      pct + '%"></div></div></div>';
  }

  async function refreshCart() {
    const cart = await getCart();
    document.querySelectorAll('[data-nb-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.dataset.count = cart.item_count;
    });
    const body = document.getElementById('nb-drawer-body');
    const total = document.getElementById('nb-drawer-total');
    if (body) {
      body.innerHTML = cart.items.length
        ? shipBarHTML(cart) + cart.items.map(lineHTML).join('')
        : '<div class="nb-drawer__empty">Your binder\u2019s empty</div>';
    }
    if (total) total.textContent = MONEY_CENTS(cart.total_price);
    hydrateCartStock(body || document);
    document.dispatchEvent(new CustomEvent('nb:cart', { detail: cart }));
    return cart;
  }

  function openDrawer() {
    if (!drawer() || drawer().classList.contains('open')) return;
    drawer().classList.add('open');
    drawerBack().classList.add('open');
    lockScroll();
  }
  function closeDrawer() {
    if (!drawer() || !drawer().classList.contains('open')) return;
    drawer().classList.remove('open');
    drawerBack().classList.remove('open');
    unlockScroll();
  }

  function hasCartQuantityIssues(scope) {
    let blocked = false;
    (scope || document).querySelectorAll('.nb-cart-line').forEach(line => {
      const max = lineMax(line);
      const input = line.querySelector('[data-act="qty"]');
      const val = input ? (parseInt(input.value, 10) || 0) : 0;
      if (max !== Infinity && val > max) {
        blocked = true;
        if (input) input.value = max;
        syncLineQty(line);
        showLineError(line, capMessage(max));
      }
    });
    return blocked;
  }

  document.addEventListener('click', e => {
    const checkout = e.target.closest('button[name="checkout"], a[href$="/checkout"]');
    if (checkout && hasCartQuantityIssues(document)) { e.preventDefault(); return; }
    if (e.target.closest('[data-nb-cart-open]')) { e.preventDefault(); refreshCart(); openDrawer(); }
    if (e.target.closest('[data-nb-drawer-close]') || e.target === drawerBack()) closeDrawer();
    const line = e.target.closest('.nb-cart-line');
    if (line) {
      const key = line.dataset.key;
      const input = line.querySelector('[data-act="qty"]');
      const act = e.target.dataset.act;
      const max = lineMax(line);
      let next = parseInt(input.value, 10) || 0;
      if (act === 'dec') next = Math.max(0, next - 1);
      if (act === 'inc') next = next + 1;
      if (act === 'rm') next = 0;
      if (act === 'inc' && max !== Infinity && next > max) {
        input.value = max; syncLineQty(line); showLineError(line, capMessage(max)); return;
      }
      if (max !== Infinity && next > max) next = max;
      if (act === 'dec' || act === 'inc' || act === 'rm') {
        showLineError(line, '');
        cartChange(key, next).catch(err => { showLineError(line, err.message || capMessage(max)); syncLineQty(line); refreshCart(); });
      }
    }
  });
  document.addEventListener('change', e => {
    const line = e.target.closest('.nb-cart-line');
    if (line && e.target.dataset.act === 'qty') {
      const max = lineMax(line);
      let next = Math.max(0, parseInt(e.target.value, 10) || 0);
      if (max !== Infinity && next > max) { next = max; e.target.value = max; showLineError(line, capMessage(max)); }
      else showLineError(line, '');
      cartChange(line.dataset.key, next).catch(err => { showLineError(line, err.message || capMessage(max)); syncLineQty(line); refreshCart(); });
    }
  });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeDrawer(); });

  /* Generic qty steppers outside cart lines */
  document.addEventListener('click', e => {
    const st = e.target.closest('.nb-qty');
    if (!st || e.target.closest('.nb-cart-line') || st.closest('[data-nb-binder]')) return;
    const input = st.querySelector('input');
    const max = input && input.max ? parseInt(input.max, 10) : Infinity;
    if (e.target.dataset.step === 'down') input.value = Math.max(1, +input.value - 1);
    if (e.target.dataset.step === 'up') input.value = Math.min(max, +input.value + 1);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });

  document.addEventListener('DOMContentLoaded', function(){ refreshCart(); hydrateCartStock(document); });

  /* ---------- Scroll lock (shared by quick view + cart drawer) ----------
     Uses position:fixed on body so iOS doesn't bleed-scroll behind modals,
     and restores the exact scroll position on unlock. Ref-counted so
     nested opens (drawer over modal) don't unlock early. */
  let _lockY = 0, _lockCount = 0;
  function lockScroll() {
    if (_lockCount === 0) {
      _lockY = window.scrollY || window.pageYOffset || 0;
      const b = document.body;
      b.style.position = 'fixed';
      b.style.top = '-' + _lockY + 'px';
      b.style.left = '0';
      b.style.right = '0';
      b.style.width = '100%';
      b.classList.add('nb-scroll-locked');
    }
    _lockCount++;
  }
  function unlockScroll() {
    _lockCount = Math.max(0, _lockCount - 1);
    if (_lockCount === 0) {
      const b = document.body;
      b.style.position = '';
      b.style.top = '';
      b.style.left = '';
      b.style.right = '';
      b.style.width = '';
      b.classList.remove('nb-scroll-locked');
      window.scrollTo(0, _lockY);
    }
  }


  /* ---------- Smart query parser ----------
     Turns "Mewtwo from destined rivals" into structured filters by matching
     fragments against values that ACTUALLY exist in the loaded catalog.
     Recognizes: set, tcg (vendor), condition, grade, and leftover card-name text.
     No AI, no network — pure structured matching over data we already have. */
  const STOP = new Set(['from','in','the','a','an','of','show','me','cards','card','all','any','with','and','for','this','that','please','i','want','need','looking','find']);
  const COND_WORDS = { 'near mint':'NM','nm':'NM','mint':'NM','lightly played':'LP','lp':'LP','light played':'LP',
    'moderately played':'MP','mp':'MP','played':'MP','heavily played':'HP','hp':'HP','damaged':'DMG','dmg':'DMG' };

  function norm(s){ return String(s||'').toLowerCase().replace(/[^a-z0-9\s]/g,' ').replace(/\s+/g,' ').trim(); }

  function parseQuery(raw, cards) {
    if (!raw || !cards || !cards.length) return null;
    let working = ' ' + norm(raw) + ' ';
    const out = { set:'', tcg:'', cond:'', grade:'', text:'' };

    /* Build vocab from the live catalog */
    const sets   = [...new Set(cards.map(c => c.set).filter(Boolean))];
    const tcgs   = [...new Set(cards.map(c => c.tcg).filter(Boolean))];
    const grades = [...new Set(cards.flatMap(c => c.variants.map(v => v.grade)).filter(Boolean))];

    /* Longest-first so "destined rivals" matches before "rivals" */
    const byLen = (a,b) => norm(b).length - norm(a).length;

    /* 1) Grade (e.g. "psa 10") */
    for (const g of grades.sort(byLen)) {
      const n = ' ' + norm(g) + ' ';
      if (working.includes(n)) { out.grade = g; working = working.replace(n, ' '); break; }
    }
    /* 2) Set */
    for (const st of sets.sort(byLen)) {
      const n = ' ' + norm(st) + ' ';
      if (n.trim() && working.includes(n)) { out.set = st; working = working.replace(n, ' '); break; }
    }
    /* 3) TCG / vendor */
    for (const t of tcgs.sort(byLen)) {
      const n = ' ' + norm(t) + ' ';
      if (n.trim() && working.includes(n)) { out.tcg = t; working = working.replace(n, ' '); break; }
    }
    /* 4) Condition phrases */
    for (const phrase of Object.keys(COND_WORDS).sort((a,b)=>b.length-a.length)) {
      const n = ' ' + phrase + ' ';
      if (working.includes(n)) { out.cond = COND_WORDS[phrase]; working = working.replace(n, ' '); break; }
    }
    /* 5) Whatever remains, minus stop words, is the card-name text */
    out.text = working.split(' ').filter(w => w && !STOP.has(w)).join(' ').trim();

    /* If nothing structured matched and there's text, just treat as a name search */
    const matchedSomething = out.set || out.tcg || out.cond || out.grade;
    if (!matchedSomething && !out.text) return null;
    return out;
  }


  /* ---------- Accent contrast guardrail ----------
     A per-binder accent may be any color. We keep button text and
     accent-on-dark readable by deriving a safe "ink on accent" color
     and a minimum luminance for accent-as-text. */
  function hexToRgb(hex) {
    if (!hex) return null;
    let h = String(hex).trim().replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    if (h.length !== 6) return null;
    return { r: parseInt(h.slice(0,2),16), g: parseInt(h.slice(2,4),16), b: parseInt(h.slice(4,6),16) };
  }
  function relLum(rgb) {
    const a = [rgb.r, rgb.g, rgb.b].map(v => {
      v /= 255; return v <= 0.03928 ? v/12.92 : Math.pow((v+0.055)/1.055, 2.4);
    });
    return 0.2126*a[0] + 0.7152*a[1] + 0.0722*a[2];
  }
  function contrast(l1, l2) { const a = Math.max(l1,l2)+0.05, b = Math.min(l1,l2)+0.05; return a/b; }
  function lighten(rgb, amt) { return { r: Math.round(rgb.r+(255-rgb.r)*amt), g: Math.round(rgb.g+(255-rgb.g)*amt), b: Math.round(rgb.b+(255-rgb.b)*amt) }; }
  function toHex(rgb){ return '#' + [rgb.r,rgb.g,rgb.b].map(v => Math.max(0,Math.min(255,v)).toString(16).padStart(2,'0')).join(''); }

  /* Returns { accent, accentText, accentBright }:
     - accent: the button background (the raw pick)
     - accentText: black or white, whichever is readable on the accent
     - accentBright: a version light enough to read as text on the dark UI */
  function darken(rgb, amt){ return { r: Math.round(rgb.r*(1-amt)), g: Math.round(rgb.g*(1-amt)), b: Math.round(rgb.b*(1-amt)) }; }
  function safeAccentVs(hex, panelLum) {
    const rgb = hexToRgb(hex);
    if (rgb == null) return null;
    const lum = relLum(rgb);
    const accentText = contrast(lum, relLum({r:0,g:0,b:0})) >= contrast(lum, relLum({r:255,g:255,b:255}))
      ? '#16130a' : '#ffffff';
    /* Make the accent read as TEXT against the live panel. On a light panel we
       darken; on a dark panel we lighten — until contrast >= 4.0. */
    const lightPanel = panelLum > 0.4;
    let bright = rgb, guard = 0;
    while (contrast(relLum(bright), panelLum) < 4.0 && guard < 14) {
      bright = lightPanel ? darken(bright, 0.12) : lighten(bright, 0.12); guard++;
    }
    return { accent: hex, accentText: accentText, accentBright: toHex(bright) };
  }
  function safeAccent(hex) { return safeAccentVs(hex, 0.012); }

  /* Apply an accent to a scope element via CSS vars (or :root if omitted). */
  function applyAccent(hex, scopeEl) {
    const el = scopeEl || document.documentElement;
    /* Read the live panel color so the guardrail works in light OR dark mode. */
    const panel = getComputedStyle(document.body).backgroundColor || 'rgb(255,255,255)';
    const pm = panel.match(/\d+/g);
    const panelLum = pm ? relLum({ r: +pm[0], g: +pm[1], b: +pm[2] }) : 1;
    const safe = safeAccentVs(hex, panelLum);
    if (!safe) return;
    el.style.setProperty('--accent', safe.accent);
    el.style.setProperty('--accent-bright', safe.accentBright);
    el.style.setProperty('--accent-ink', safe.accentText);
  }


  return {
    MONEY, MONEY_CENTS, esc, sized, condCode, condClass, COND_ORDER,
    toCard, bestVar, badgeHTML, chipsHTML, Card3D,
    cartAdd, cartChange, getCart, refreshCart, openDrawer, closeDrawer, cartErrorMessage,
    safeAccent, safeAccentVs, applyAccent,
    parseQuery, registerStock,
    lockScroll, unlockScroll,
    reduced
  };
})();
